/**
 * Master Data Edit Form Module
 */

import { FormBase } from "../util/form-base-form";

type MasterDataEditFormScalar = string | number | boolean | null | undefined | File;

export type MasterDataEditFormValue = MasterDataEditFormScalar | FormDataObject | MasterDataEditFormValue[];

export type FormDataObject = { [key: string]: MasterDataEditFormValue };

class MasterDataEditFormBase extends FormBase {
    public override fromEventTarget<T extends Element = HTMLElement>(
        root: Event | EventTarget | Element | null | undefined,
        elementClass?: abstract new (...args: never[]) => T,
    ): T | null {
        return super.fromEventTarget(root, elementClass);
    }

    public findClosest<T extends Element = HTMLElement>(
        root: Event | EventTarget | Element | null | undefined,
        selector: string,
        elementClass?: abstract new (...args: never[]) => T,
    ): T | null {
        let current = this.fromEventTarget(root, Element);

        while (current instanceof Element) {
            if (typeof current.matches === "function" && current.matches(selector)) {
                return current instanceof (elementClass ?? Element) ? (current as T) : null;
            }
            current = current.parentElement;
        }

        return null;
    }

    public override queryElement<T extends Element = HTMLElement>(
        root: ParentNode | null | undefined,
        selector: string,
        elementClass?: abstract new (...args: never[]) => T,
    ): T | null {
        return super.queryElement(root, selector, elementClass);
    }

    public override queryElements<T extends Element = HTMLElement>(
        root: ParentNode | null | undefined,
        selector: string,
        elementClass?: abstract new (...args: never[]) => T,
    ): T[] {
        return super.queryElements(root, selector, elementClass);
    }
}

const formBase = new MasterDataEditFormBase();

function qs(root: ParentNode, selector: string): HTMLElement | null {
    return formBase.queryElement(root, selector, HTMLElement);
}

function getEventTarget(event: Event): Element | null {
    return formBase.fromEventTarget(event, Element);
}

function getClosest(root: Element | Event | null | undefined, selector: string): HTMLElement | null {
    return formBase.findClosest(root, selector, HTMLElement);
}

function qsa(root: ParentNode, selector: string): HTMLElement[] {
    return formBase.queryElements(root, selector, HTMLElement);
}

function getConfigScript(root: ParentNode, role: string): HTMLScriptElement | null {
    return formBase.queryElement(root, `script[data-role="${role}"]`, HTMLScriptElement);
}

function getEditorRoots(role: string, root: ParentNode = document): HTMLElement[] {
    return formBase.queryElements(root, `[data-role="${role}"]`, HTMLElement);
}

function getRoleElement(root: ParentNode, role: string): HTMLElement | null {
    return formBase.queryElement(root, `[data-role="${role}"]`, HTMLElement);
}

function getRoleElements(root: ParentNode, role: string): HTMLElement[] {
    return formBase.queryElements(root, `[data-role="${role}"]`, HTMLElement);
}

function getRoleScript(root: ParentNode, role: string): HTMLScriptElement | null {
    return formBase.queryElement(root, `[data-role="${role}"]`, HTMLScriptElement);
}

function getRoleSelect(root: ParentNode, role: string): HTMLSelectElement | null {
    return formBase.queryElement(root, `select[data-role="${role}"]`, HTMLSelectElement);
}

function getRoleButton(root: ParentNode, role: string): HTMLButtonElement | null {
    return formBase.queryElement(root, `[data-role="${role}"]`, HTMLButtonElement);
}

function getRoleImage(root: ParentNode, role: string): HTMLImageElement | null {
    return formBase.queryElement(root, `[data-role="${role}"]`, HTMLImageElement);
}

function getOptionByValue(root: ParentNode, value: string): HTMLOptionElement | null {
    return formBase.queryElement(root, `option[value="${value}"]`, HTMLOptionElement);
}

function getSubmitControl(root: ParentNode): HTMLButtonElement | HTMLInputElement | null {
    return (
        formBase.queryElement(root, 'button[type="submit"]', HTMLButtonElement) ||
        formBase.queryElement(root, 'input[type="submit"]', HTMLInputElement)
    );
}

function setValue(form: HTMLFormElement, name: string, value: MasterDataEditFormValue): void {
    const el = form.elements.namedItem(name);
    if (!el) return;

    if (typeof RadioNodeList !== "undefined" && el instanceof RadioNodeList) {
        const normalized = value === null || value === undefined ? "" : String(value);
        el.value = normalized;

        const nodes: HTMLInputElement[] = [];
        for (let index = 0; index < el.length; index += 1) {
            const node = el.item(index);
            if (node instanceof HTMLInputElement) {
                nodes.push(node);
            }
        }
        const hasCheckbox = nodes.some((node) => node.type === "checkbox");

        nodes.forEach((node) => {
            if (hasCheckbox) {
                if (node.type === "checkbox") {
                    node.checked = normalized === "1" || normalized.toLowerCase() === "true";
                    return;
                }

                if (node.type === "hidden") {
                    node.value = "0";
                    return;
                }

                node.value = normalized;
                return;
            }

            if (node.type === "radio") {
                node.checked = node.value === normalized;
                return;
            }

            node.value = normalized;
        });
        return;
    }

    if (el instanceof HTMLInputElement) {
        if (el.type === "checkbox") {
            const normalized = value === true || value === 1 || value === "1" || value === "true";
            el.checked = Boolean(normalized);
            return;
        }
        el.value = value === null || value === undefined ? "" : String(value);
        return;
    }

    if (el instanceof HTMLTextAreaElement) {
        el.value = value === null || value === undefined ? "" : String(value);
        return;
    }

    if (el instanceof HTMLSelectElement) {
        if (el.multiple) {
            let selected: string[] = [];

            if (Array.isArray(value)) {
                selected = value
                    .map((entry) => (entry === null || entry === undefined ? "" : String(entry).trim()))
                    .filter(Boolean);
            } else if (typeof value === "string") {
                const raw = value.trim();
                if (raw) {
                    if (raw.startsWith("[") && raw.endsWith("]")) {
                        try {
                            const parsed = JSON.parse(raw) as unknown;
                            if (Array.isArray(parsed)) {
                                selected = parsed
                                    .map((entry) => (entry === null || entry === undefined ? "" : String(entry).trim()))
                                    .filter(Boolean);
                            }
                        } catch {
                            // fall back to CSV/scalar parse
                        }
                    }

                    if (selected.length === 0) {
                        selected = raw.includes(",")
                            ? raw
                                  .split(",")
                                  .map((entry) => entry.trim())
                                  .filter(Boolean)
                            : [raw];
                    }
                }
            } else if (value !== null && value !== undefined) {
                selected = [String(value).trim()].filter(Boolean);
            }

            const existingValues = new Set(
                Array.from(el.options)
                    .map((option) => String(option.value).trim())
                    .filter(Boolean),
            );

            selected.forEach((entry) => {
                if (existingValues.has(entry)) return;

                const option = new Option(entry, entry, false, true);
                el.add(option);
                existingValues.add(entry);
            });

            const selectedSet = new Set(selected);
            Array.from(el.options).forEach((option) => {
                option.selected = selectedSet.has(option.value);
            });

            el.dispatchEvent(new Event("change", { bubbles: true }));
            return;
        }

        el.value = value === null || value === undefined ? "" : String(value);
    }
}

function getFormDataObject(form: HTMLFormElement): FormDataObject {
    const data: FormDataObject = {};
    const formData = new FormData(form);
    for (const [key, value] of formData.entries()) {
        if (!Object.prototype.hasOwnProperty.call(data, key)) {
            data[key] = value;
            continue;
        }

        const current = data[key];
        if (Array.isArray(current)) {
            current.push(value);
            continue;
        }

        data[key] = [current, value];
    }
    return data;
}

export {
    qs,
    qsa,
    getEventTarget,
    getClosest,
    getConfigScript,
    getEditorRoots,
    getRoleElement,
    getRoleElements,
    getRoleScript,
    getRoleSelect,
    getRoleButton,
    getRoleImage,
    getOptionByValue,
    getSubmitControl,
    setValue,
    getFormDataObject,
};
